import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  IconButton,
  Tooltip,
  FormControlLabel,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Button,
  Grid,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons';
import CustomSwitch from 'src/components/forms/theme-elements/CustomSwitch';
import { visuallyHidden } from '@mui/utils';
import { Delete } from '@mui/icons-material';
import AlertBox from 'src/components/material-ui/dialog/AlertBox';
import {
  DisabledDeleteIcon,
  DisabledEditIcon,
  Spinner,
  WithPermission,
} from 'src/components/common';
import EmptyState from 'src/components/common/EmptyState/EmptyState';
import { bankDelt, bankEdit, bankList, bankSelector } from 'src/store/apps/slices/BankSlice';
import { BANK_CREATE_SK, BANK_DELETE_SK, BANK_EDIT_SK, LIMIT } from 'src/lib/constants';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'no',
    numeric: false,
    disablePadding: false,
    label: 'No',
  },
  {
    id: 'bank_name',
    numeric: false,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              <Typography fontWeight={800}> {headCell.label} </Typography>

              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { handleSearch, search, handleClickOpen, handleSearchClick } = props;
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 2, sm: 2 },
      }}
    >
      <Box sx={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch size="1.1rem" />
              </InputAdornment>
            ),
          }}
          placeholder="Search with Name"
          size="medium"
          onChange={handleSearch}
          value={search}
          sx={{ minWidth: '20%' }}
        />
        <Button
          variant="contained"
          sx={{ height: 'fit-content' }}
          color="primary"
          onClick={handleSearchClick} // Trigger API call
        >
          Search
        </Button>
      </Box>
      <WithPermission permission={BANK_CREATE_SK}>
      <Tooltip title="Add New Bank">
        <Button color="primary" onClick={handleClickOpen} variant="contained">
          <IconPlus size="1.3rem" icon="filter" /> Add
        </Button>
      </Tooltip>
      </WithPermission>
    </Toolbar>
  );
};

const BankList = ({ handleClickOpen, setBank }) => {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(LIMIT.ROWS_PER_PAGE_OPTIONS[0]);
  const dispatch = useDispatch();
  const { pagination, loading, datas, submitLoading } = useSelector(bankSelector);
  const [rows, setRows] = React.useState(datas);
  const [search, setSearch] = React.useState('');
  const [keyword, setKeyword] = React.useState(''); // Actual keyword used for API call
  const [open, setOpen] = React.useState(false);
  const [row, setRow] = React.useState('');
  const title = `Are you sure to delete ${row?.bank_name}!`;

  React.useEffect(() => {
    dispatch(
      bankList({
        perPage: rowsPerPage,
        page: page + 1,
        keyword: keyword,
      }),
    );
  }, [dispatch, page, rowsPerPage, keyword]);

  React.useEffect(() => {
    setRows(datas);
  }, [datas]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchClick = () => {
    setKeyword(search); // Trigger API call by updating keyword
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const handleConfirm = () => {
    row?.id &&
      dispatch(bankDelt(row?.id)).then((res) => {
        if (res.payload.status === 200) {
          setOpen(false);
          setRow('');
        }
      });
  };

  const handleDelete = (bank) => {
    setRow(bank);
    setOpen(true);
  };

  const handleChangeActivation = (bank) => {
    const data = {
      bank_name: bank?.bank_name,
      is_active: bank?.is_active === true ? false : true,
    };
    if (bank) {
      dispatch(bankEdit({ id: bank?.id, data: data }));
    }
  };

  return (
    <Box>
      <Box>
        <EnhancedTableToolbar
          search={search}
          handleSearch={(event) => handleSearch(event)}
          handleClickOpen={handleClickOpen}
          handleSearchClick={handleSearchClick}
        />
        <Paper variant="outlined" sx={{ mx: 2, mt: 1 }}>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center" sx={{ height: 380 }}>
                      <Spinner />
                    </TableCell>
                  </TableRow>
                ) : !loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center" sx={{ height: 380 }}>
                      <EmptyState
                        title={keyword ? `No results found for "${keyword}"` : 'No banks added yet'}
                        buttonText="Add New Bank"
                        buttonAction={handleClickOpen}
                        permission={BANK_CREATE_SK}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(rows, getComparator(order, orderBy)).map((row, index) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        <TableCell>
                          <Typography fontWeight={500}>{page * rowsPerPage + index + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>{row?.bank_name}</Typography>
                        </TableCell>

                        <TableCell>
                          <WithPermission permission={BANK_EDIT_SK}>
                            <FormControlLabel
                              control={
                                <CustomSwitch
                                  checked={row?.is_active === true}
                                  onChange={() => handleChangeActivation(row)}
                                />
                              }
                            />
                          </WithPermission>
                          <WithPermission permission={BANK_EDIT_SK} fallback={<DisabledEditIcon />}>
                            <Tooltip title="Edit" sx={{ marginRight: '10px' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setBank(row);
                                  handleClickOpen();
                                }}
                              >
                                <IconEdit size="1.1rem" />
                              </IconButton>
                            </Tooltip>
                          </WithPermission>
                          <WithPermission
                            permission={BANK_DELETE_SK}
                            fallback={<DisabledDeleteIcon />}
                          >
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  handleDelete(row);
                                }}
                              >
                                <IconTrash size="1.1rem" />
                              </IconButton>
                            </Tooltip>
                          </WithPermission>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={LIMIT.ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={pagination?.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <Box ml={2}>
          <FormControlLabel
            control={<CustomSwitch checked={dense} onChange={handleChangeDense} />}
            label="Dense padding"
          />
        </Box>
        {open && (
          <AlertBox
            open={open}
            handleClose={() => setOpen(false)}
            handleConfirm={handleConfirm}
            title={title}
            disabled={submitLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default BankList;
